import { useState } from 'react';
import { Paper, Modal, Stack, Text, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
	useGetAllWellBeingSurveyQuestionsQuery,
	useUpdateWellBeingSurveyQuestionTranslationsMutation,
} from '@codezo/graphql';
import { DataContainer, ScrollContainer } from '@codezo/ui';
import { WellBeingQuestionsList } from '../components/WellBeingQuestionsList';
import { WellBeingTranslationsForm } from '../components/WellBeingTranslationsForm';

export function WellBeingPage() {
	const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
	const [updateModalOpened, setUpdateModalOpened] = useState(false);

	// Fetch questions with translations
	const { data, loading, refetch } = useGetAllWellBeingSurveyQuestionsQuery();
	const [updateTranslations, { loading: updating }] =
		useUpdateWellBeingSurveyQuestionTranslationsMutation();

	const handleEdit = (question: any) => {
		setSelectedQuestion(question);
		setUpdateModalOpened(true);
	};

	const handleUpdateSubmit = async (values: any) => {
		try {
			await updateTranslations({
				variables: {
					input: {
						translations: values.translations,
					},
				},
			});

			notifications.show({
				title: 'Success',
				message: 'Survey question translations updated successfully',
				color: 'green',
			});

			setUpdateModalOpened(false);
			setSelectedQuestion(null);
			refetch();
		} catch (error: any) {
			notifications.show({
				title: 'Error',
				message: error.message || 'Failed to update translations',
				color: 'red',
			});
		}
	};

	const handleCancel = () => {
		setUpdateModalOpened(false);
		setSelectedQuestion(null);
	};

	// Transform question data to include translations in object format
	// Support both old and new query formats during migration
	const questions =
		data?.getAllWellBeingSurveyQuestionsWithTranslations?.map((question) => {
			// Convert translations array to object keyed by language
			const translationsMap = question.translations.reduce(
				(acc, trans) => ({
					...acc,
					[trans.language]: { text: trans.text },
				}),
				{}
			);

			return {
				...question,
				text: question.translations.find((t) => t.language === 'en')?.text || '',
				translations: translationsMap,
			};
		}) ||
		// Fallback to old format if new query hasn't been generated yet
		(data as any)?.en?.map((enQuestion: any, index: number) => ({
			...enQuestion,
			translations: {
				en: { text: enQuestion.text },
				da: { text: (data as any).da?.[index]?.text || '' },
			},
		})) ||
		[];

	return (
		<ScrollContainer>
			<Stack gap="lg">
				<Group justify="space-between">
					<div>
						<Text size="lg" fw={600}>
							Well-Being Survey
						</Text>
						<Text size="sm" c="dimmed">
							Manage WHO-5 Well-Being Index question translations
						</Text>
					</div>
				</Group>

				<DataContainer loading={loading} error={undefined}>
					<Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
						<WellBeingQuestionsList questions={questions} onEdit={handleEdit} />
					</Paper>
				</DataContainer>
			</Stack>

			<Modal
				opened={updateModalOpened}
				onClose={handleCancel}
				title="Update Question Translations"
				size="lg"
			>
				{selectedQuestion && (
					<WellBeingTranslationsForm
						question={selectedQuestion}
						onSubmit={handleUpdateSubmit}
						onCancel={handleCancel}
						loading={updating}
					/>
				)}
			</Modal>
		</ScrollContainer>
	);
}
